import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";

import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useGapFillStore } from "@/stores/gap-fill-store";
import type { Gap } from "@/types/audit";
import type {
  GapFillAcceptResponse,
  GapFillCompletePayload,
} from "@/types/gapFill";

interface StartResponse {
  job_id: string;
  total_steps: number;
}

interface ActiveJobResponse {
  job_id: string;
  concept_id: string;
  status: string;
  step: number;
  step_label: string;
  total_steps: number;
  error: string | null;
}

const STEP_FALLBACK_LABELS: Record<number, string> = {
  0: "Preparing…",
  1: "Searching",
  2: "Scoring",
  3: "Drafting gap-fit captions",
};

async function consumeGapFillStream(
  conceptId: string,
  jobId: string,
  signal: AbortSignal,
  onComplete: (payload: GapFillCompletePayload) => void,
  onError: (msg: string) => void,
): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
  const token = await getAuthToken();
  const url = `${baseUrl}/api/concepts/${conceptId}/gap-fill/generate/${jobId}/stream`;
  const store = useGapFillStore;

  let receivedTerminal = false;
  try {
    for await (const event of streamSSE(
      url,
      { Authorization: `Bearer ${token}` },
      signal,
    )) {
      const data = event.data ? JSON.parse(event.data) : {};

      if (event.event.startsWith("step:")) {
        const [, stepRaw, kind] = event.event.split(":");
        const step = Number(stepRaw);
        // Also handle `skipped` + `done` for symmetry with audit hook —
        // pill reflects step transitions even on fast paths.
        if (kind === "start" || kind === "skipped" || kind === "done") {
          store
            .getState()
            .setStep(
              step as 0 | 1 | 2 | 3,
              data.label || STEP_FALLBACK_LABELS[step] || "",
            );
        }
        continue;
      }
      if (event.event === "complete") {
        receivedTerminal = true;
        onComplete(data as GapFillCompletePayload);
        return;
      }
      if (event.event === "error") {
        receivedTerminal = true;
        onError(data.error || "Gap-fill failed");
        return;
      }
      if (event.event === "cancelled") {
        receivedTerminal = true;
        store.getState().reset();
        toast.info("Gap-fill cancelled");
        return;
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") return;
    onError(err instanceof Error ? err.message : "SSE failed");
    return;
  }
  if (!receivedTerminal) onError("Stream closed before completing");
}

export function useGapFillGenerate(conceptId: string) {
  const queryClient = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  const handleComplete = useCallback(
    (payload: GapFillCompletePayload) => {
      // Restore gap + audit_id from the payload before flipping isOpen.
      // Resume-on-mount paths lost the original context to a hard
      // refresh; without this the modal would render null. Safe on the
      // happy path too — overwriting with the same values is a no-op.
      useGapFillStore
        .getState()
        .setContext(payload.audit_id, payload.gap);
      useGapFillStore.getState().setCandidates(payload.candidates);
      useGapFillStore.getState().finish();
      if (payload.candidates.length === 0) {
        toast.info("No papers matched these queries");
      } else {
        toast.success(`${payload.candidates.length} candidates ready`);
      }
    },
    [],
  );

  const handleError = useCallback((msg: string) => {
    useGapFillStore.getState().fail(msg);
    toast.error("Gap-fill failed", { description: msg });
  }, []);

  const start = useCallback(
    async (auditId: string, gap: Gap) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Optimistic: surface progress immediately. start() spreads
      // initialState, so re-set cancelController right after.
      useGapFillStore.getState().start(conceptId, "", auditId, gap);
      useGapFillStore.getState().setCancelController(controller);

      // Cancel any existing active gap-fill job for this concept.
      try {
        const active = await apiClient
          .get(`api/concepts/${conceptId}/gap-fill/generate/active`, {
            timeout: 10_000,
          })
          .json<ActiveJobResponse | null>();
        if (
          active &&
          (active.status === "pending" || active.status === "running")
        ) {
          await apiClient.post(
            `api/concepts/${conceptId}/gap-fill/generate/${active.job_id}/cancel`,
            { timeout: 10_000 },
          );
        }
      } catch {
        // proceed — best-effort
      }
      if (controller.signal.aborted) return;

      try {
        const res = await apiClient
          .post(`api/concepts/${conceptId}/gap-fill/generate`, {
            json: { audit_id: auditId, gap },
            timeout: 30_000,
          })
          .json<StartResponse>();
        if (controller.signal.aborted) return;

        // Re-arm after start() spread.
        useGapFillStore.getState().start(conceptId, res.job_id, auditId, gap);
        useGapFillStore.getState().setCancelController(controller);

        await consumeGapFillStream(
          conceptId,
          res.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        if ((err as { name?: string })?.name === "AbortError") return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409) {
          handleError("A gap-fill is already running — wait or cancel it.");
          return;
        }
        handleError(
          err instanceof Error ? err.message : "Failed to start gap-fill",
        );
      }
    },
    [conceptId, handleComplete, handleError],
  );

  const cancel = useCallback(async () => {
    const { jobId, cancelController } = useGapFillStore.getState();
    (cancelController ?? abortRef.current)?.abort();
    useGapFillStore.getState().reset();
    if (!jobId) return;
    try {
      await apiClient.post(
        `api/concepts/${conceptId}/gap-fill/generate/${jobId}/cancel`,
      );
    } catch {
      // best-effort
    }
  }, [conceptId]);

  // Resume on mount.
  useEffect(() => {
    const controller = new AbortController();
    let attached = false;
    (async () => {
      try {
        const active = await apiClient
          .get(`api/concepts/${conceptId}/gap-fill/generate/active`, {
            timeout: 10_000,
          })
          .json<ActiveJobResponse | null>();
        if (!active || controller.signal.aborted) return;
        const store = useGapFillStore.getState();
        if (store.jobId === active.job_id && store.isPending) return;
        // Note: gap context is not in the /active response; we can't restore
        // gap/audit_id from the server probe alone. If the user navigated
        // away after starting, the resume will pick up the SSE stream but
        // the modal context (audit_id, gap) won't be restored — accept will
        // show with blank metadata. Acceptable for v1.
        attached = true;
        useGapFillStore
          .getState()
          .setStep(
            (active.step ?? 0) as 0 | 1 | 2 | 3,
            active.step_label || STEP_FALLBACK_LABELS[active.step ?? 0] || "",
          );
        abortRef.current?.abort();
        abortRef.current = controller;
        await consumeGapFillStream(
          conceptId,
          active.job_id,
          controller.signal,
          handleComplete,
          handleError,
        );
      } catch {
        // 404 / no active job — fine
      }
    })();
    return () => {
      if (!attached) controller.abort();
    };
  }, [conceptId, handleComplete, handleError]);

  const acceptMutation = useMutation({
    mutationFn: async (paperIds: string[]) => {
      const { auditId, gap, candidates } = useGapFillStore.getState();
      if (!auditId || !gap) throw new Error("Missing audit context");
      // Send the full selected candidate objects so the backend can
      // stamp score data + per-paper source_query on the new junctions
      // (no rescore needed; provenance complete).
      const selected = candidates
        .filter((c) => paperIds.includes(c.paper_id))
        .map((c) => ({
          paper_id: c.paper_id,
          score: c.score,
          methodology_score: c.methodology_score,
          contribution_type: c.contribution_type,
          argumentation_role: c.argumentation_role,
          relevance_justification: c.relevance_justification,
          source_query: c.source_query,
        }));
      return apiClient
        .post(`api/concepts/${conceptId}/gap-fill/accept`, {
          json: { audit_id: auditId, gap, candidates: selected },
          timeout: 60_000,
        })
        .json<GapFillAcceptResponse>();
    },
    onSuccess: (data) => {
      const tag = useGapFillStore.getState().gap?.tag ?? "gap";
      toast.success(`Added ${data.added} papers from gap "${tag}"`);
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
      queryClient.invalidateQueries({
        queryKey: ["concept-papers", conceptId],
      });
      // Full reset on accept — closes modal AND clears the OperationBar
      // pill in one step (no 5s auto-fade wait).
      useGapFillStore.getState().reset();
    },
    onError: (err) => {
      toast.error("Failed to add papers", {
        description: err instanceof Error ? err.message : String(err),
      });
    },
  });

  return { start, cancel, accept: acceptMutation };
}
